import { useTranslation } from 'react-i18next';
import { useApp } from '../../hooks/AppProvider';
import { useSession } from '../../hooks/SessionProvider';
import type { ResultEvent } from '../../types/events';
import isAdmin from '../../utils/isAdmin';
import deriveBaseUrl from '../../utils/deriveBaseUrl';
import getCurrentHostname from '../../utils/getCurrentHostname';
import buildServiceUrl from '../../utils/buildServiceUrl';
import removePortFromUrl from '../../utils/removePortFromUrl';
import type { NavLinkProps } from '@fastnorth/kraken/components/navigation';
import { ReceiptText, Timer } from 'lucide-react';
import { Link } from 'react-router-dom';

const useUserLinks = (): NavLinkProps[] => {
  const { t } = useTranslation('translation', { keyPrefix: 'karwa' });
  const [{ event }] = useApp();
  const [{ session }] = useSession();
  const { roles } = session || {};

  const { eid } = (event || {}) as unknown as ResultEvent;

  const baseEnv = deriveBaseUrl(removePortFromUrl(getCurrentHostname()));

  const adminLinkUrl = buildServiceUrl({
    subdomain: 'timer',
    baseEnv,
    targetPort: '5050',
    path: `/dashboard/results/rr-${eid}`,
  });

  const participantHomeUrl = buildServiceUrl({
    baseEnv,
    targetPort: '5652',
    path: '/dashboard/welcome#registrations',
  });

  // Define link definitions with declarative conditions
  const linkDefinitions = [
    {
      shouldDisplay: !!roles && isAdmin(roles),
      getLink: () => ({
        id: 'results-admin',
        children: (
          <Link
            to={adminLinkUrl}
            className={'flex items-center'}
            data-dd-action-name={'link-results_admin-hamburger_menu'}>
            <Timer size={16} strokeWidth={2} className={'mr-4'} />
            {t('components.user_menu_content.admin_link_label')}
          </Link>
        ),
      }),
    },
    {
      shouldDisplay: true, // Always show
      getLink: () => ({
        id: 'participant-home',
        children: (
          <Link
            to={participantHomeUrl}
            className={'flex items-center'}
            data-dd-action-name={'link-participant_homepage-hamburger_menu'}>
            <ReceiptText size={16} strokeWidth={2} className={'mr-4'} />
            {t('components.user_menu_content.participant_link_label')}
          </Link>
        ),
      }),
    },
  ];

  // Generate links from definitions
  return linkDefinitions.filter((def) => def.shouldDisplay).map((def) => def.getLink());
};

export default useUserLinks;
